import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { fetchWithTimeout, isTimeoutError, isNetworkError } from '../utils/networkError';
import { Link2, Copy, Check, ExternalLink, Loader2, AlertTriangle, Info, Eye, X, Star } from 'lucide-react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { SortableDoctorItem } from '../components/SortableDoctorItem';
import ClinicLayout from '../components/ClinicLayout';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
const FRONTEND_URL = import.meta.env.VITE_FRONTEND_URL || window.location.origin;

const ClinicLinkInBio = () => {
  const { user, token, loading: authLoading, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // State
  const [clinicId, setClinicId] = useState(null);
  const [currentSlug, setCurrentSlug] = useState('');
  const [newSlug, setNewSlug] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [slugAvailable, setSlugAvailable] = useState(null);
  const [slugError, setSlugError] = useState('');
  const [slugFormatError, setSlugFormatError] = useState('');
  const [copied, setCopied] = useState(false);
  const [showWarning, setShowWarning] = useState(false);

  // Display mode state
  const [displayMode, setDisplayMode] = useState('booking');
  const [isSavingDisplayMode, setIsSavingDisplayMode] = useState(false);
  const [displayModeSuccess, setDisplayModeSuccess] = useState('');

  // Custom bio state
  const [currentBio, setCurrentBio] = useState('');
  const [newBio, setNewBio] = useState('');
  const [isSavingBio, setIsSavingBio] = useState(false);
  const [bioError, setBioError] = useState('');
  const [bioSuccessMessage, setBioSuccessMessage] = useState('');

  // Show doctors state
  const [showDoctors, setShowDoctors] = useState(true);
  const [isSavingShowDoctors, setIsSavingShowDoctors] = useState(false);
  const [showDoctorsError, setShowDoctorsError] = useState('');
  const [showDoctorsSuccessMessage, setShowDoctorsSuccessMessage] = useState('');

  // Show reviews state
  const [showReviews, setShowReviews] = useState(true);
  const [isSavingShowReviews, setIsSavingShowReviews] = useState(false);
  const [showReviewsError, setShowReviewsError] = useState('');
  const [showReviewsSuccessMessage, setShowReviewsSuccessMessage] = useState('');

  // Doctor visibility state
  const [doctors, setDoctors] = useState([]);
  const [isLoadingDoctors, setIsLoadingDoctors] = useState(false);
  const [isSavingDoctorVisibility, setIsSavingDoctorVisibility] = useState(false);
  const [doctorVisibilityError, setDoctorVisibilityError] = useState('');
  const [doctorVisibilitySuccess, setDoctorVisibilitySuccess] = useState('');

  // Doctor reordering state
  const [isSavingDoctorOrder, setIsSavingDoctorOrder] = useState(false);
  const [doctorOrderError, setDoctorOrderError] = useState('');
  const [doctorOrderSuccess, setDoctorOrderSuccess] = useState('');

  // Preview state
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Redirect if not authenticated or not a clinic owner
  useEffect(() => {
    if (!authLoading && (!isAuthenticated || user?.role !== 'clinic_owner')) {
      navigate('/login');
    }
  }, [authLoading, isAuthenticated, user, navigate]);

  // Fetch current slug
  const fetchSlug = useCallback(async () => {
    if (!isAuthenticated || user?.role !== 'clinic_owner' || !token) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetchWithTimeout(
        `${API_URL}/clinics/my/slug`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        },
        15000
      );

      if (!response.ok) {
        throw new Error('Failed to fetch slug');
      }

      const data = await response.json();
      setClinicId(data.clinicId);
      setCurrentSlug(data.slug || '');
      setNewSlug(data.slug || '');

      // Fetch display mode
      const displayModeResponse = await fetchWithTimeout(
        `${API_URL}/clinics/my/display-mode`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        },
        15000
      );

      if (displayModeResponse.ok) {
        const displayModeData = await displayModeResponse.json();
        setDisplayMode(displayModeData.displayMode || 'booking');
      }

      // Fetch custom bio
      const bioResponse = await fetchWithTimeout(
        `${API_URL}/clinics/my/link-in-bio-bio`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        },
        15000
      );

      if (bioResponse.ok) {
        const bioData = await bioResponse.json();
        setCurrentBio(bioData.linkInBioBio || '');
        setNewBio(bioData.linkInBioBio || '');
      }

      // Fetch show doctors setting
      const showDoctorsResponse = await fetchWithTimeout(
        `${API_URL}/clinics/my/show-doctors`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        },
        15000
      );

      if (showDoctorsResponse.ok) {
        const showDoctorsData = await showDoctorsResponse.json();
        setShowDoctors(showDoctorsData.showDoctors ?? true);
      }

      // Fetch show reviews setting
      const showReviewsResponse = await fetchWithTimeout(
        `${API_URL}/clinics/my/show-reviews`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        },
        15000
      );

      if (showReviewsResponse.ok) {
        const showReviewsData = await showReviewsResponse.json();
        setShowReviews(showReviewsData.showReviews ?? true);
      }
    } catch (err) {
      console.error('Error fetching slug:', err);
      if (isTimeoutError(err)) {
        setError('Request timed out. Please try again.');
      } else if (isNetworkError(err)) {
        setError('Unable to connect to the server.');
      } else {
        setError(err.message);
      }
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, user, token]);

  useEffect(() => {
    if (isAuthenticated && user?.role === 'clinic_owner' && token) {
      fetchSlug();
    }
  }, [isAuthenticated, user, token, fetchSlug]);

  // Fetch doctor visibility settings when display mode is 'all_doctors'
  const fetchDoctorVisibility = useCallback(async () => {
    if (!isAuthenticated || user?.role !== 'clinic_owner' || !token || displayMode !== 'all_doctors') {
      return;
    }

    setIsLoadingDoctors(true);
    setDoctorVisibilityError('');

    try {
      const response = await fetchWithTimeout(
        `${API_URL}/clinics/my/doctor-visibility`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        },
        15000
      );

      if (!response.ok) {
        throw new Error('Failed to fetch doctor visibility settings');
      }

      const data = await response.json();
      setDoctors(data.doctors || []);
    } catch (err) {
      console.error('Error fetching doctor visibility:', err);
      if (isTimeoutError(err)) {
        setDoctorVisibilityError('Request timed out. Please try again.');
      } else if (isNetworkError(err)) {
        setDoctorVisibilityError('Unable to connect to the server.');
      } else {
        setDoctorVisibilityError(err.message);
      }
    } finally {
      setIsLoadingDoctors(false);
    }
  }, [isAuthenticated, user, token, displayMode]);

  useEffect(() => {
    if (displayMode === 'all_doctors' && token) {
      fetchDoctorVisibility();
    }
  }, [displayMode, token, fetchDoctorVisibility]);

  // Validate slug format - returns error message or null if valid
  const validateSlugFormat = (value) => {
    if (!value) return null; // Empty is fine, we'll check for that separately

    // Check for uppercase letters
    if (/[A-Z]/.test(value)) {
      return 'Uppercase letters are not allowed. Please use lowercase only.';
    }

    // Check for spaces
    if (/\s/.test(value)) {
      return 'Spaces are not allowed. Use hyphens (-) instead.';
    }

    // Check for special characters (anything other than lowercase, numbers, hyphens)
    if (/[^a-z0-9-]/.test(value)) {
      return 'Only lowercase letters, numbers, and hyphens are allowed.';
    }

    // Check for consecutive hyphens
    if (/--/.test(value)) {
      return 'Consecutive hyphens are not allowed.';
    }

    // Check if starts or ends with hyphen
    if (value.startsWith('-') || value.endsWith('-')) {
      return 'Slug cannot start or end with a hyphen.';
    }

    // Check minimum length
    if (value.length < 3) {
      return 'Slug must be at least 3 characters long.';
    }

    // Check maximum length
    if (value.length > 50) {
      return 'Slug must be at most 50 characters long.';
    }

    return null; // Valid
  };

  // Check slug availability with debounce
  useEffect(() => {
    const checkSlug = async () => {
      const slug = newSlug.trim();

      // Don't check if there's a format error
      if (slugFormatError) {
        setSlugAvailable(null);
        setSlugError('');
        return;
      }

      // Don't check if empty, same as current, or too short
      if (!slug || slug === currentSlug || slug.length < 3) {
        setSlugAvailable(null);
        setSlugError('');
        return;
      }

      setIsChecking(true);
      setSlugError('');

      try {
        const response = await fetchWithTimeout(
          `${API_URL}/clinics/check-slug/${encodeURIComponent(slug)}`,
          {},
          10000
        );

        const data = await response.json();

        if (data.available) {
          setSlugAvailable(true);
          setSlugError('');
        } else {
          setSlugAvailable(false);
          setSlugError(data.error || 'This slug is already taken');
        }
      } catch (err) {
        console.error('Error checking slug:', err);
        setSlugAvailable(null);
        setSlugError('Could not check availability');
      } finally {
        setIsChecking(false);
      }
    };

    const timeoutId = setTimeout(checkSlug, 500);
    return () => clearTimeout(timeoutId);
  }, [newSlug, currentSlug, slugFormatError]);

  // Handle slug input change - don't auto-correct, show validation errors instead
  const handleSlugChange = (e) => {
    const value = e.target.value;
    setNewSlug(value);
    setSuccessMessage('');

    // Validate and show format error
    const formatError = validateSlugFormat(value);
    setSlugFormatError(formatError);

    // Clear availability status when format is invalid
    if (formatError) {
      setSlugAvailable(null);
      setSlugError('');
    }
  };

  // Handle save slug
  const handleSaveSlug = async (e) => {
    e.preventDefault();

    const slug = newSlug.trim();
    if (!slug || slug === currentSlug) return;

    const formatError = validateSlugFormat(slug);
    if (formatError) {
      setSlugFormatError(formatError);
      return;
    }

    // Show warning if changing an existing slug
    if (currentSlug && slug !== currentSlug) {
      setShowWarning(true);
      return;
    }

    // Proceed with save
    await performSave(slug);
  };

  // Perform the actual save operation
  const performSave = async (slug) => {
    setShowWarning(false);
    setIsSaving(true);
    setError(null);
    setSuccessMessage('');

    try {
      const response = await fetchWithTimeout(
        `${API_URL}/clinics/my/slug`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ slug })
        },
        15000
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to update slug');
      }

      const data = await response.json();
      setCurrentSlug(data.slug);
      setNewSlug(data.slug);
      setSuccessMessage('Slug updated successfully!');
      setSlugAvailable(null);
      setSlugError('');
    } catch (err) {
      console.error('Error saving slug:', err);
      if (isTimeoutError(err)) {
        setError('Request timed out. Please try again.');
      } else if (isNetworkError(err)) {
        setError('Unable to connect to the server.');
      } else {
        setError(err.message);
      }
    } finally {
      setIsSaving(false);
    }
  };

  // Handle cancel warning
  const handleCancelWarning = () => {
    setShowWarning(false);
    setNewSlug(currentSlug); // Reset to current slug
  };

  // Handle confirm warning
  const handleConfirmWarning = () => {
    performSave(newSlug.trim());
  };

  // Handle copy URL
  const handleCopyUrl = async () => {
    if (!currentSlug) return;

    const url = `${FRONTEND_URL}/${currentSlug}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Handle display mode change
  const handleDisplayModeChange = async (newMode) => {
    setIsSavingDisplayMode(true);
    setError(null);
    setDisplayModeSuccess('');

    try {
      const response = await fetchWithTimeout(
        `${API_URL}/clinics/my/display-mode`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ displayMode: newMode })
        },
        15000
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to update display mode');
      }

      setDisplayMode(newMode);
      setDisplayModeSuccess('Display mode updated successfully!');
      setTimeout(() => setDisplayModeSuccess(''), 3000);
    } catch (err) {
      console.error('Error saving display mode:', err);
      if (isTimeoutError(err)) {
        setError('Request timed out. Please try again.');
      } else if (isNetworkError(err)) {
        setError('Unable to connect to the server.');
      } else {
        setError(err.message);
      }
    } finally {
      setIsSavingDisplayMode(false);
    }
  };

  // Handle save custom bio
  const handleSaveBio = async () => {
    setIsSavingBio(true);
    setBioError('');
    setBioSuccessMessage('');

    try {
      const response = await fetchWithTimeout(
        `${API_URL}/clinics/my/link-in-bio-bio`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ linkInBioBio: newBio.trim() || null })
        },
        15000
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to save custom bio');
      }

      setCurrentBio(newBio.trim());
      setBioSuccessMessage('Your custom bio has been saved!');
      setTimeout(() => setBioSuccessMessage(''), 5000);
    } catch (err) {
      console.error('Error saving custom bio:', err);
      if (isTimeoutError(err)) {
        setBioError('Request timed out. Please try again.');
      } else if (isNetworkError(err)) {
        setBioError('Unable to connect to the server.');
      } else {
        setBioError(err.message);
      }
    } finally {
      setIsSavingBio(false);
    }
  };

  // Handle toggle doctors visibility
  const handleToggleShowDoctors = async (newValue) => {
    setIsSavingShowDoctors(true);
    setShowDoctorsError('');
    setShowDoctorsSuccessMessage('');

    try {
      const response = await fetchWithTimeout(
        `${API_URL}/clinics/my/show-doctors`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ showDoctors: newValue })
        },
        15000
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update doctors visibility');
      }

      setShowDoctors(newValue);
      setShowDoctorsSuccessMessage(
        newValue
          ? 'Doctors section is now visible on your link-in-bio page'
          : 'Doctors section is now hidden from your link-in-bio page'
      );
      setTimeout(() => setShowDoctorsSuccessMessage(''), 5000);
    } catch (err) {
      console.error('Error updating doctors visibility:', err);
      if (isTimeoutError(err)) {
        setShowDoctorsError('Request timed out. Please try again.');
      } else if (isNetworkError(err)) {
        setShowDoctorsError('Unable to connect to the server.');
      } else {
        setShowDoctorsError(err.message);
      }
      // Revert the toggle on error
      setShowDoctors(!newValue);
    } finally {
      setIsSavingShowDoctors(false);
    }
  };

  // Handle toggle reviews visibility
  const handleToggleShowReviews = async (newValue) => {
    setIsSavingShowReviews(true);
    setShowReviewsError('');
    setShowReviewsSuccessMessage('');

    try {
      const response = await fetchWithTimeout(
        `${API_URL}/clinics/my/show-reviews`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ showReviews: newValue })
        },
        15000
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update reviews visibility');
      }

      setShowReviews(newValue);
      setShowReviewsSuccessMessage(
        newValue
          ? 'Reviews section is now visible on your link-in-bio page'
          : 'Reviews section is now hidden from your link-in-bio page'
      );
      setTimeout(() => setShowReviewsSuccessMessage(''), 5000);
    } catch (err) {
      console.error('Error updating reviews visibility:', err);
      if (isTimeoutError(err)) {
        setShowReviewsError('Request timed out. Please try again.');
      } else if (isNetworkError(err)) {
        setShowReviewsError('Unable to connect to the server.');
      } else {
        setShowReviewsError(err.message);
      }
      // Revert the toggle on error
      setShowReviews(!newValue);
    } finally {
      setIsSavingShowReviews(false);
    }
  };

  // Handle toggle individual doctor visibility
  const handleToggleDoctorVisibility = (doctorId) => {
    setDoctors(prevDoctors =>
      prevDoctors.map(doc =>
        doc.id === doctorId ? { ...doc, isVisible: !doc.isVisible } : doc
      )
    );
  };

  // Handle save doctor visibility
  const handleSaveDoctorVisibility = async () => {
    setIsSavingDoctorVisibility(true);
    setDoctorVisibilityError('');
    setDoctorVisibilitySuccess('');

    try {
      const doctorVisibility = doctors.map(doc => ({
        doctorId: doc.id,
        isVisible: doc.isVisible,
      }));

      const response = await fetchWithTimeout(
        `${API_URL}/clinics/my/doctor-visibility`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ doctorVisibility })
        },
        15000
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update doctor visibility');
      }

      setDoctorVisibilitySuccess('Doctor visibility updated successfully!');
      setTimeout(() => setDoctorVisibilitySuccess(''), 5000);
    } catch (err) {
      console.error('Error updating doctor visibility:', err);
      if (isTimeoutError(err)) {
        setDoctorVisibilityError('Request timed out. Please try again.');
      } else if (isNetworkError(err)) {
        setDoctorVisibilityError('Unable to connect to the server.');
      } else {
        setDoctorVisibilityError(err.message);
      }
    } finally {
      setIsSavingDoctorVisibility(false);
    }
  };

  // Handle drag end for doctor reordering
  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setDoctors((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);

        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  // Handle save doctor order
  const handleSaveDoctorOrder = async () => {
    setIsSavingDoctorOrder(true);
    setDoctorOrderError('');
    setDoctorOrderSuccess('');

    try {
      const doctorOrder = doctors.map((doc, index) => ({
        doctorId: doc.id,
        displayOrder: index,
      }));

      const response = await fetchWithTimeout(
        `${API_URL}/clinics/my/doctors/order`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ doctorOrder })
        },
        15000
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update doctor order');
      }

      setDoctorOrderSuccess('Doctor order updated successfully!');
      setTimeout(() => setDoctorOrderSuccess(''), 5000);
    } catch (err) {
      console.error('Error updating doctor order:', err);
      if (isTimeoutError(err)) {
        setDoctorOrderError('Request timed out. Please try again.');
      } else if (isNetworkError(err)) {
        setDoctorOrderError('Unable to connect to the server.');
      } else {
        setDoctorOrderError(err.message);
      }
    } finally {
      setIsSavingDoctorOrder(false);
    }
  };

  // Handle preview
  const handlePreview = async () => {
    if (!clinicId) return;

    setShowPreview(true);
    setIsLoadingPreview(true);
    setPreviewError(null);

    try {
      // Fetch clinic profile using the public endpoint
      const response = await fetchWithTimeout(
        `${API_URL}/clinics/${clinicId}/profile`,
        {},
        15000
      );

      if (!response.ok) {
        throw new Error('Failed to load preview');
      }

      const data = await response.json();

      // Apply unsaved changes from the current form state to the preview data
      const previewWithChanges = {
        ...data,
        clinic: {
          ...data.clinic,
          // Use the current form values for unsaved changes (use newBio even if empty string)
          linkInBioBio: newBio,
          displayMode: displayMode,
          showDoctors: showDoctors,
          showReviews: showReviews,
        },
        // Use the current doctors array which may have reordered or visibility changes
        // Only use the local doctors array if we're in all_doctors mode (otherwise it won't be loaded)
        doctors: displayMode === 'all_doctors' && doctors.length > 0
          ? doctors.filter(d => d.isVisible !== false)
          : data.doctors,
      };

      setPreviewData(previewWithChanges);
    } catch (err) {
      console.error('Error loading preview:', err);
      if (isTimeoutError(err)) {
        setPreviewError('Request timed out. Please try again.');
      } else if (isNetworkError(err)) {
        setPreviewError('Unable to connect to the server.');
      } else {
        setPreviewError(err.message);
      }
    } finally {
      setIsLoadingPreview(false);
    }
  };

  // Handle close preview
  const handleClosePreview = () => {
    setShowPreview(false);
    setPreviewData(null);
    setPreviewError(null);
  };

  // Loading state
  if (authLoading || isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-5rem)]">
        <Loader2 className="w-8 h-8 animate-spin text-light-text dark:text-pure-white" />
      </div>
    );
  }

  // Not authenticated or not a clinic owner
  if (!isAuthenticated || user?.role !== 'clinic_owner') {
    return null;
  }

  const linkInBioUrl = currentSlug ? `${FRONTEND_URL}/${currentSlug}` : '';
  const hasChanges = newSlug !== currentSlug;
  const canSave = hasChanges && !slugFormatError && slugAvailable === true;

  return (
    <ClinicLayout>
      <div className="px-4 py-8">
        <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/clinic/dashboard"
            className="inline-flex items-center gap-2 text-sm text-light-text-secondary dark:text-silver/60 hover:text-light-text dark:hover:text-pure-white transition-colors mb-4"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
            </svg>
            Back to Dashboard
          </Link>

          <div className="flex items-center gap-3 mb-3">
            <Link2 className="w-8 h-8 text-light-text dark:text-pure-white" />
            <h1 className="font-display text-4xl italic tracking-tighter leading-tight">
              Link-in-Bio
            </h1>
          </div>
          <p className="font-body text-light-text-secondary dark:text-silver/60">
            Create a custom short URL for your clinic that you can share on social media
          </p>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
            <button
              onClick={() => setError(null)}
              className="text-red-400 hover:text-red-300 transition-colors"
            >
              ✕
            </button>
          </div>
        )}

        {successMessage && (
          <div className="mb-6 p-4 bg-green-500/10 border border-green-500/30 rounded-xl flex items-start gap-3">
            <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
            <p className="text-green-400 text-sm">{successMessage}</p>
          </div>
        )}

        {/* Main Content */}
        <div className="space-y-6">
          {/* Current URL Display */}
          {currentSlug && (
            <div className="glass border border-light-border dark:border-border rounded-2xl p-6">
              <p className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-3">
                Your Link-in-Bio URL
              </p>
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-1 p-4 bg-slate-50 dark:bg-white/5 border border-light-border dark:border-border rounded-xl">
                  <p className="font-mono text-sm break-all">{linkInBioUrl}</p>
                </div>
                <button
                  onClick={handleCopyUrl}
                  className="p-4 border border-light-border dark:border-border rounded-xl hover:border-slate-300 dark:hover:border-white/20 transition-smooth"
                  title="Copy URL"
                >
                  {copied ? (
                    <Check className="w-5 h-5 text-green-400" />
                  ) : (
                    <Copy className="w-5 h-5" />
                  )}
                </button>
                <a
                  href={linkInBioUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-4 border border-light-border dark:border-border rounded-xl hover:border-slate-300 dark:hover:border-white/20 transition-smooth"
                  title="Open in new tab"
                >
                  <ExternalLink className="w-5 h-5" />
                </a>
              </div>

              {/* Preview Button */}
              <button
                onClick={handlePreview}
                className="w-full px-6 py-3 bg-gradient-silver text-obsidian rounded-xl font-mono text-xs uppercase tracking-widest hover:shadow-glow transition-smooth flex items-center justify-center gap-2"
              >
                <Eye className="w-4 h-4" />
                Preview Page
              </button>
            </div>
          )}

          {/* Slug Editor */}
          <div className="glass border border-light-border dark:border-border rounded-2xl p-6">
            <form onSubmit={handleSaveSlug}>
              <label className="block font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-3">
                Custom URL Slug
              </label>

              <div className="flex items-start gap-3 mb-4">
                <div className="flex-1">
                  <div className="flex items-center border border-light-border dark:border-border rounded-xl overflow-hidden focus-within:border-slate-300 dark:focus-within:border-white/20 transition-colors">
                    <span className="px-4 py-3 bg-slate-50 dark:bg-white/5 border-r border-light-border dark:border-border font-mono text-sm text-slate-500 dark:text-silver/60">
                      {FRONTEND_URL.replace(/^https?:\/\//, '')}/
                    </span>
                    <input
                      type="text"
                      value={newSlug}
                      onChange={handleSlugChange}
                      placeholder="my-clinic"
                      className="flex-1 px-4 py-3 bg-transparent outline-none font-mono text-sm"
                      disabled={isSaving}
                    />
                    {isChecking && (
                      <div className="px-4">
                        <Loader2 className="w-4 h-4 animate-spin text-slate-400 dark:text-silver/40" />
                      </div>
                    )}
                    {!isChecking && !slugFormatError && slugAvailable === true && newSlug !== currentSlug && (
                      <div className="px-4">
                        <Check className="w-4 h-4 text-green-400" />
                      </div>
                    )}
                    {!isChecking && (slugFormatError || slugAvailable === false) && (
                      <div className="px-4">
                        <AlertTriangle className="w-4 h-4 text-red-400" />
                      </div>
                    )}
                  </div>

                  {/* Validation Messages */}
                  {slugFormatError && (
                    <p className="mt-2 text-sm text-red-400 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      {slugFormatError}
                    </p>
                  )}
                  {!slugFormatError && slugError && (
                    <p className="mt-2 text-sm text-red-400 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      {slugError}
                    </p>
                  )}
                  {!slugFormatError && !slugError && slugAvailable === true && (
                    <p className="mt-2 text-sm text-green-400 flex items-center gap-2">
                      <Check className="w-4 h-4" />
                      This slug is available!
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={!canSave || isSaving}
                  className="px-6 py-3 bg-gradient-silver text-obsidian rounded-xl font-mono text-xs uppercase tracking-widest hover:shadow-glow transition-smooth disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-none whitespace-nowrap"
                >
                  {isSaving ? 'Saving...' : 'Save Slug'}
                </button>
              </div>

              {/* Info Box */}
              <div className="flex items-start gap-3 p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
                <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1 text-sm text-blue-400">
                  <p className="font-medium mb-1">Slug Requirements:</p>
                  <ul className="list-disc list-inside space-y-1 text-xs">
                    <li>3-50 characters long</li>
                    <li>Only lowercase letters, numbers, and hyphens</li>
                    <li>Must start and end with a letter or number</li>
                    <li>Cannot use reserved words (admin, api, login, etc.)</li>
                  </ul>
                </div>
              </div>
            </form>
          </div>

          {/* Display Mode Configuration */}
          <div className="glass border border-light-border dark:border-border rounded-2xl p-6">
            <h2 className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-3">
              Display Mode
            </h2>

            {displayModeSuccess && (
              <div className="mb-4 p-3 bg-green-500/10 border border-green-500/30 rounded-xl flex items-center gap-2">
                <Check className="w-4 h-4 text-green-400" />
                <p className="text-green-400 text-sm">{displayModeSuccess}</p>
              </div>
            )}

            <div className="space-y-3">
              {/* Booking Mode Option */}
              <button
                onClick={() => handleDisplayModeChange('booking')}
                disabled={isSavingDisplayMode}
                className={`w-full p-4 border rounded-xl text-left transition-smooth ${
                  displayMode === 'booking'
                    ? 'border-gradient-silver bg-white/5'
                    : 'border-light-border dark:border-border hover:border-silver/40'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                    displayMode === 'booking'
                      ? 'border-gradient-silver bg-gradient-silver'
                      : 'border-light-border dark:border-border'
                  }`}>
                    {displayMode === 'booking' && (
                      <div className="w-2 h-2 rounded-full bg-obsidian"></div>
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="font-mono text-sm font-medium mb-1">Booking Interface</p>
                    <p className="text-xs text-light-text-secondary dark:text-silver/60">
                      Show a booking interface where patients can select a doctor and book appointments directly.
                      Best for clinics with round-robin or simple booking flows.
                    </p>
                  </div>
                </div>
              </button>

              {/* All Doctors Mode Option */}
              <button
                onClick={() => handleDisplayModeChange('all_doctors')}
                disabled={isSavingDisplayMode}
                className={`w-full p-4 border rounded-xl text-left transition-smooth ${
                  displayMode === 'all_doctors'
                    ? 'border-gradient-silver bg-white/5'
                    : 'border-light-border dark:border-border hover:border-silver/40'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                    displayMode === 'all_doctors'
                      ? 'border-gradient-silver bg-gradient-silver'
                      : 'border-light-border dark:border-border'
                  }`}>
                    {displayMode === 'all_doctors' && (
                      <div className="w-2 h-2 rounded-full bg-obsidian"></div>
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="font-mono text-sm font-medium mb-1">Show All Doctors</p>
                    <p className="text-xs text-light-text-secondary dark:text-silver/60">
                      Display a list of all your clinic's doctors with their profiles, specialties, and reviews.
                      Patients can browse and select their preferred doctor.
                    </p>
                  </div>
                </div>
              </button>
            </div>
          </div>

          {/* Doctor Visibility Section (only show when display mode is 'all_doctors') */}
          {displayMode === 'all_doctors' && (
            <div className="glass border border-light-border dark:border-border rounded-2xl p-6">
              <h2 className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-3">
                Select Doctors to Display
              </h2>
              <p className="font-body text-xs text-light-text-secondary dark:text-silver/60 mb-4">
                Choose which doctors appear on your link-in-bio page when showing all doctors.
              </p>

              {doctorVisibilitySuccess && (
                <div className="mb-4 p-3 bg-green-500/10 border border-green-500/30 rounded-xl flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-400" />
                  <p className="text-green-400 text-sm">{doctorVisibilitySuccess}</p>
                </div>
              )}

              {doctorVisibilityError && (
                <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-xl flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-400" />
                  <p className="text-red-400 text-sm">{doctorVisibilityError}</p>
                </div>
              )}

              {isLoadingDoctors ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-light-text dark:text-pure-white" />
                </div>
              ) : doctors.length === 0 ? (
                <div className="p-4 bg-slate-50 dark:bg-white/5 border border-light-border dark:border-border rounded-xl text-center">
                  <p className="font-body text-sm text-light-text-secondary dark:text-silver/60">
                    No doctors in your clinic yet. Add doctors to show them on your link-in-bio page.
                  </p>
                </div>
              ) : (
                <>
                  <div className="space-y-3 mb-4">
                    {doctors.map((doctor) => (
                      <div
                        key={doctor.id}
                        className="flex items-center gap-3 p-3 border border-light-border dark:border-border rounded-xl hover:border-silver/40 transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={doctor.isVisible}
                          onChange={() => handleToggleDoctorVisibility(doctor.id)}
                          className="w-5 h-5 rounded border-2 border-light-border dark:border-border checked:bg-gradient-silver checked:border-gradient-silver cursor-pointer"
                          disabled={isSavingDoctorVisibility}
                        />
                        <div className="flex-1 flex items-center gap-3">
                          {doctor.photoUrl ? (
                            <img
                              src={doctor.photoUrl}
                              alt={doctor.name}
                              className="w-10 h-10 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-gradient-silver flex items-center justify-center">
                              <span className="text-obsidian font-mono text-sm font-bold">
                                {doctor.firstName[0]}{doctor.lastName[0]}
                              </span>
                            </div>
                          )}
                          <div className="flex-1">
                            <p className="font-mono text-sm font-medium">{doctor.name}</p>
                            {doctor.specialties && doctor.specialties.length > 0 && (
                              <p className="text-xs text-light-text-secondary dark:text-silver/60">
                                {doctor.specialties.join(', ')}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <button
                    onClick={handleSaveDoctorVisibility}
                    disabled={isSavingDoctorVisibility}
                    className="w-full px-6 py-3 bg-gradient-silver text-obsidian rounded-xl font-mono text-xs uppercase tracking-widest hover:shadow-glow transition-smooth disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-none"
                  >
                    {isSavingDoctorVisibility ? 'Saving...' : 'Save Changes'}
                  </button>
                </>
              )}
            </div>
          )}

          {/* Doctor Reordering Section (only show when display mode is 'all_doctors') */}
          {displayMode === 'all_doctors' && doctors.length > 0 && (
            <div className="glass border border-light-border dark:border-border rounded-2xl p-6">
              <h2 className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-3">
                Reorder Doctors
              </h2>
              <p className="font-body text-xs text-light-text-secondary dark:text-silver/60 mb-4">
                Drag and drop to change the order in which doctors appear on your link-in-bio page.
              </p>

              {doctorOrderSuccess && (
                <div className="mb-4 p-3 bg-green-500/10 border border-green-500/30 rounded-xl flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-400" />
                  <p className="text-green-400 text-sm">{doctorOrderSuccess}</p>
                </div>
              )}

              {doctorOrderError && (
                <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-xl flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-400" />
                  <p className="text-red-400 text-sm">{doctorOrderError}</p>
                </div>
              )}

              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={doctors.map(d => d.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-3 mb-4">
                    {doctors.map((doctor) => (
                      <SortableDoctorItem key={doctor.id} doctor={doctor} />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>

              <button
                onClick={handleSaveDoctorOrder}
                disabled={isSavingDoctorOrder}
                className="w-full px-6 py-3 bg-gradient-silver text-obsidian rounded-xl font-mono text-xs uppercase tracking-widest hover:shadow-glow transition-smooth disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-none"
              >
                {isSavingDoctorOrder ? 'Saving...' : 'Save Order'}
              </button>
            </div>
          )}

          {/* Custom Bio Section */}
          <div className="glass border border-light-border dark:border-border rounded-2xl p-6">
            <h2 className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-3">
              Custom Clinic Description
            </h2>
            <p className="font-body text-xs text-light-text-secondary dark:text-silver/60 mb-4">
              Add a custom description for your link-in-bio page. This will be shown instead of your clinic's default description.
            </p>

            {bioSuccessMessage && (
              <div className="mb-4 p-3 bg-green-500/10 border border-green-500/30 rounded-xl flex items-center gap-2">
                <Check className="w-4 h-4 text-green-400" />
                <p className="text-green-400 text-sm">{bioSuccessMessage}</p>
              </div>
            )}

            {bioError && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-xl flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-400" />
                <p className="text-red-400 text-sm">{bioError}</p>
              </div>
            )}

            <textarea
              value={newBio}
              onChange={(e) => setNewBio(e.target.value)}
              placeholder="Tell patients about your clinic..."
              className="w-full px-4 py-3 bg-slate-50 dark:bg-white/5 border border-light-border dark:border-border rounded-xl outline-none focus:border-slate-300 dark:focus:border-white/20 transition-colors resize-none font-body text-sm"
              rows={4}
              disabled={isSavingBio}
            />

            <div className="flex justify-between items-center mt-3">
              <p className="font-mono text-xs text-light-text-secondary dark:text-silver/60">
                {newBio.length} characters
              </p>
              <button
                onClick={handleSaveBio}
                disabled={isSavingBio || newBio === currentBio}
                className="px-6 py-3 bg-gradient-silver text-obsidian rounded-xl font-mono text-xs uppercase tracking-widest hover:shadow-glow transition-smooth disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-none"
              >
                {isSavingBio ? 'Saving...' : 'Save Bio'}
              </button>
            </div>
          </div>

          {/* Section Visibility Toggles */}
          <div className="glass border border-light-border dark:border-border rounded-2xl p-6">
            <h2 className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-3">
              Section Visibility
            </h2>
            <p className="font-body text-xs text-light-text-secondary dark:text-silver/60 mb-4">
              Control which sections appear on your link-in-bio page
            </p>

            {showDoctorsSuccessMessage && (
              <div className="mb-4 p-3 bg-green-500/10 border border-green-500/30 rounded-xl flex items-center gap-2">
                <Check className="w-4 h-4 text-green-400" />
                <p className="text-green-400 text-sm">{showDoctorsSuccessMessage}</p>
              </div>
            )}

            {showDoctorsError && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-xl flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-400" />
                <p className="text-red-400 text-sm">{showDoctorsError}</p>
              </div>
            )}

            {showReviewsSuccessMessage && (
              <div className="mb-4 p-3 bg-green-500/10 border border-green-500/30 rounded-xl flex items-center gap-2">
                <Check className="w-4 h-4 text-green-400" />
                <p className="text-green-400 text-sm">{showReviewsSuccessMessage}</p>
              </div>
            )}

            {showReviewsError && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-xl flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-400" />
                <p className="text-red-400 text-sm">{showReviewsError}</p>
              </div>
            )}

            <div className="space-y-3">
              {/* Show Doctors Toggle */}
            <div className="flex items-center justify-between p-4 border border-light-border dark:border-border rounded-xl">
              <div>
                <p className="font-mono text-sm font-medium mb-1">Show Doctors List</p>
                <p className="text-xs text-light-text-secondary dark:text-silver/60">
                  Display your clinic's doctors on the link-in-bio page (only when display mode is "Show All Doctors")
                </p>
              </div>
              <button
                onClick={() => handleToggleShowDoctors(!showDoctors)}
                disabled={isSavingShowDoctors}
                className={`relative w-12 h-6 rounded-full transition-colors ${
                  showDoctors ? 'bg-gradient-silver' : 'bg-slate-300 dark:bg-white/20'
                } ${isSavingShowDoctors ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <div
                  className={`absolute top-1 left-1 w-4 h-4 rounded-full bg-obsidian transition-transform ${
                    showDoctors ? 'translate-x-6' : 'translate-x-0'
                  }`}
                ></div>
              </button>
            </div>

              {/* Show Reviews Toggle */}
              <div className="flex items-center justify-between p-4 border border-light-border dark:border-border rounded-xl">
                <div>
                  <p className="font-mono text-sm font-medium mb-1">Show Reviews</p>
                  <p className="text-xs text-light-text-secondary dark:text-silver/60">
                    Display aggregated reviews and ratings from all your clinic's doctors
                  </p>
                </div>
                <button
                  onClick={() => handleToggleShowReviews(!showReviews)}
                  disabled={isSavingShowReviews}
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    showReviews ? 'bg-gradient-silver' : 'bg-slate-300 dark:bg-white/20'
                  } ${isSavingShowReviews ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <div
                    className={`absolute top-1 left-1 w-4 h-4 rounded-full bg-obsidian transition-transform ${
                      showReviews ? 'translate-x-6' : 'translate-x-0'
                    }`}
                  ></div>
                </button>
              </div>
            </div>
          </div>

          {/* Usage Examples */}
          <div className="glass border border-light-border dark:border-border rounded-2xl p-6">
            <h2 className="font-mono text-sm uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-4">
              How to Use Your Link-in-Bio
            </h2>
            <div className="space-y-3 text-sm text-light-text-secondary dark:text-silver/80">
              <p>
                Share this link on your social media profiles, email signatures, and marketing materials.
                It provides a clean, memorable URL that directs patients to your clinic's booking page.
              </p>
              <p className="font-body">
                <span className="font-semibold">Examples:</span>
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Add to Instagram/Twitter/LinkedIn bio</li>
                <li>Include in email signatures</li>
                <li>Print on business cards and flyers</li>
                <li>Share in online directories</li>
              </ul>
            </div>
          </div>

          {/* Preview Modal */}
          {showPreview && (
            <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
              <div className="glass border border-light-border dark:border-border rounded-2xl w-full max-w-3xl my-8">
                <div className="flex items-center justify-between p-6 border-b border-light-border dark:border-border">
                  <div className="flex items-center gap-3">
                    <Eye className="w-6 h-6 text-light-text dark:text-pure-white" />
                    <h3 className="font-display text-2xl italic tracking-tighter">
                      Preview: Link-in-Bio Page
                    </h3>
                  </div>
                  <button
                    onClick={handleClosePreview}
                    className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                    title="Close preview"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <div className="p-6">
                  {isLoadingPreview && (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin text-light-text dark:text-pure-white" />
                    </div>
                  )}

                  {previewError && (
                    <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                      <p className="text-red-400 text-sm">{previewError}</p>
                    </div>
                  )}

                  {!isLoadingPreview && !previewError && previewData && (
                    <div className="space-y-6">
                      <div className="bg-slate-50 dark:bg-white/5 border border-light-border dark:border-border rounded-2xl p-8">
                        <div className="flex items-start gap-6">
                          {previewData.clinic.photoUrl && (
                            <img
                              src={previewData.clinic.photoUrl}
                              alt={previewData.clinic.name}
                              className="w-24 h-24 rounded-2xl object-cover border border-light-border dark:border-border"
                            />
                          )}
                          <div className="flex-1">
                            <h1 className="font-display text-4xl italic tracking-tighter leading-tight mb-2">
                              {previewData.clinic.name}
                            </h1>
                            {(previewData.clinic.linkInBioBio || previewData.clinic.description) && (
                              <p className="font-body text-light-text-secondary dark:text-silver/80 leading-relaxed">
                                {previewData.clinic.linkInBioBio || previewData.clinic.description}
                              </p>
                            )}
                            <p className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mt-4">
                              {previewData.clinic.doctorsCount} {previewData.clinic.doctorsCount === 1 ? 'Doctor' : 'Doctors'}
                            </p>
                          </div>
                        </div>
                      </div>

                      {previewData.clinic.displayMode === 'all_doctors' && previewData.clinic.showDoctors !== false && previewData.doctors && previewData.doctors.length > 0 && (
                        <div className="space-y-4">
                          <h2 className="font-display text-2xl italic tracking-tighter">Our Doctors</h2>
                          {previewData.doctors.map((doctor) => (
                            <div key={doctor.id} className="bg-slate-50 dark:bg-white/5 border border-light-border dark:border-border rounded-2xl p-6">
                              <div className="flex items-start gap-4">
                                {doctor.photoUrl ? (
                                  <img src={doctor.photoUrl} alt={doctor.name} className="w-20 h-20 rounded-2xl object-cover border border-light-border dark:border-border" />
                                ) : (
                                  <div className="w-20 h-20 rounded-2xl bg-gradient-silver flex items-center justify-center border border-light-border dark:border-border">
                                    <span className="font-display text-2xl text-obsidian">{doctor.firstName?.[0]}{doctor.lastName?.[0]}</span>
                                  </div>
                                )}
                                <div className="flex-1">
                                  <h3 className="font-display text-xl italic tracking-tighter mb-1">{doctor.name}</h3>
                                  {doctor.specialties && doctor.specialties.length > 0 && (
                                    <div className="flex flex-wrap gap-2 mb-2">
                                      {doctor.specialties.map((specialty, idx) => (
                                        <span key={idx} className="font-mono text-xs uppercase tracking-widest px-2 py-1 bg-white/5 border border-light-border dark:border-border rounded">{specialty}</span>
                                      ))}
                                    </div>
                                  )}
                                  {doctor.bio && <p className="font-body text-sm text-light-text-secondary dark:text-silver/80 line-clamp-2 mb-2">{doctor.bio}</p>}
                                  {doctor.reviewCount > 0 && (
                                    <div className="flex items-center gap-2">
                                      <div className="flex items-center gap-1">
                                        <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                                        <span className="font-mono text-sm">{doctor.averageRating.toFixed(1)}</span>
                                      </div>
                                      <span className="font-mono text-xs text-light-text-secondary dark:text-silver/60">({doctor.reviewCount} {doctor.reviewCount === 1 ? 'review' : 'reviews'})</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {previewData.clinic.displayMode === 'booking' && (
                        <div className="bg-slate-50 dark:bg-white/5 border border-light-border dark:border-border rounded-2xl p-8 text-center">
                          <h2 className="font-display text-2xl italic tracking-tighter mb-4">Book an Appointment</h2>
                          {previewData.clinic.bookingMode === 'round_robin' ? (
                            <p className="font-body text-light-text-secondary dark:text-silver/80 mb-6">We will automatically assign you to the next available doctor.</p>
                          ) : (
                            <p className="font-body text-light-text-secondary dark:text-silver/80 mb-6">Select a doctor to book an appointment.</p>
                          )}
                          <div className="inline-block px-6 py-3 bg-gradient-silver text-obsidian rounded-xl font-mono text-xs uppercase tracking-widest">
                            {previewData.clinic.bookingMode === 'round_robin' ? 'Book with Auto-Assignment' : 'View Doctors & Book'}
                          </div>
                        </div>
                      )}

                      <div className="flex items-start gap-3 p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
                        <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                        <div className="flex-1 text-sm text-blue-400">
                          <p>This is a preview of how your page will look to visitors. Any changes you make in the settings will be reflected here after saving.</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-end gap-3 p-6 border-t border-light-border dark:border-border">
                  <button onClick={handleClosePreview} className="px-6 py-3 border border-light-border dark:border-border rounded-xl font-mono text-xs uppercase tracking-widest hover:border-silver/40 transition-smooth">Close</button>
                  <a href={linkInBioUrl} target="_blank" rel="noopener noreferrer" className="px-6 py-3 bg-gradient-silver text-obsidian rounded-xl font-mono text-xs uppercase tracking-widest hover:shadow-glow transition-smooth flex items-center gap-2">
                    <ExternalLink className="w-4 h-4" />
                    Open Full Page
                  </a>
                </div>
              </div>
            </div>
          )}


          {/* Warning Modal */}
          {showWarning && (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
              <div className="glass border border-light-border dark:border-border rounded-2xl p-6 max-w-md w-full">
                <div className="flex items-start gap-3 mb-4">
                  <AlertTriangle className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-display text-2xl italic tracking-tighter mb-2">
                      Warning: Old Links Will Break
                    </h3>
                    <p className="font-body text-sm text-light-text-secondary dark:text-silver/60">
                      Changing your URL slug will break all existing links that use your old slug.
                    </p>
                    <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                      <p className="font-body text-xs text-yellow-200">
                        <strong>Current:</strong> <span className="font-mono">{FRONTEND_URL}/{currentSlug}</span>
                      </p>
                      <p className="font-body text-xs text-yellow-200 mt-1">
                        <strong>New:</strong> <span className="font-mono">{FRONTEND_URL}/{newSlug}</span>
                      </p>
                    </div>
                    <p className="font-body text-xs text-light-text-secondary dark:text-silver/60 mt-3">
                      Anyone who bookmarked or shared your old link will need to be notified of the new URL.
                    </p>
                  </div>
                </div>
                <div className="flex gap-3 justify-end">
                  <button
                    onClick={handleCancelWarning}
                    className="px-4 py-2 border border-light-border dark:border-border rounded-lg font-mono text-xs uppercase tracking-widest hover:border-silver/40 transition-smooth"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirmWarning}
                    className="px-4 py-2 bg-yellow-500 text-obsidian rounded-lg font-mono text-xs uppercase tracking-widest hover:bg-yellow-400 transition-smooth"
                  >
                    I Understand, Change It
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
        </div>
      </div>
    </ClinicLayout>
  );
};

export default ClinicLinkInBio;
